import { remark } from "remark";
import remarkGfm from "remark-gfm";
import remarkHtml from "remark-html";
import { Interweave } from "interweave";
import { Link } from "./Link";
import clsx from "clsx";

const transformNode = (
    node: HTMLElement,
    children: React.ReactNode,
): React.ReactNode => {
    if (node.tagName === "HR") {
        return <hr className="border-default my-4" />;
    }
    if (node.tagName === "H2") {
        return <h2 className="heading-m mb-2">{children}</h2>;
    }
    if (node.tagName === "H3") {
        return <h3 className="heading-s">{children}</h3>;
    }
    if (node.tagName === "H4") {
        return <h4 className="subheading">{children}</h4>;
    }
    if (node.tagName === "UL") {
        return (
            <ul className="my-4 list-outside list-disc space-y-1">
                {children}
            </ul>
        );
    }
    if (node.tagName === "LI") {
        return <li className="body-s ml-4">{children}</li>;
    }

    if (node.tagName === "EM") {
        return <em className="text-gray-400">{children}</em>;
    }

    if (node.tagName === "P") {
        const links = Array.from(node.querySelectorAll("a"));
        if (links.length > 1) {
            return (
                <div
                    className={clsx(
                        "mb-4 mt-1",
                        "flex flex-wrap",
                        "subdued gap-3",
                    )}
                >
                    {links.map((linkElement, index) => (
                        <Link
                            key={index}
                            href={linkElement.getAttribute("href") || ""}
                            title={linkElement.textContent || ""}
                        />
                    ))}
                </div>
            );
        }
        return <p className="body-s subdued mb-4 mt-1">{children}</p>;
    }

    if (node.tagName === "A") {
        const parentP = node.closest("p");
        const allLinksInP = parentP?.querySelectorAll("a") || [];

        if (allLinksInP.length > 1) return null;

        return (
            <Link
                href={node.getAttribute("href") || ""}
                title={node.textContent || ""}
            />
        );
    }

    return undefined;
};

export const MarkdownMemo = ({ content }: { content: string }) => {
    if (!content) return null;

    const processedContent = remark()
        .use(remarkGfm)
        .use(remarkHtml)
        .processSync(content);

    const htmlContent = processedContent.toString();

    return (
        <div className="prose">
            <Interweave
                content={htmlContent}
                tagName="div"
                transform={transformNode}
            />
        </div>
    );
};
